import { ErrorMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TaxFreeMessage } from 'modules/checkout/contacts/TaxIdInput';
import { Country } from 'modules/data/countries/model';
import { TaxRate } from 'modules/data/taxRates/model';
import { Field } from 'modules/form';
import t from 'modules/i18n/intl';
import { Dimensions } from 'components/visuals';
import Help from 'components/common/Help';
import {
  checkIsCountryCanada,
  checkIsCountryEgypt,
  getVATResponsibleCopies,
  taxExemptionsLabels,
} from 'modules/utils';
import BillingDetailsTaxIdRadio from './MACContactDetailsTaxIdRadio';

type TaxIdFieldProps = {
  country: Country | undefined;
  taxRate: TaxRate;
  showTaxId: boolean;
  taxIdLabel: string;
  vatResponsible: boolean | null;
  vatStatus?: 'CORPORATE' | 'INDIVIDUAL' | null;
  taxId: string;
  taxExemptions: string;
  shouldShowVATResponsibilitySection?: boolean;
  handleFormChange: (e, fieldName) => void;
  setBillingDetailsForm: (value) => void;
  taxIdValidationResult: any;
  state: string;
};

export default ({
  taxRate,
  showTaxId,
  taxIdLabel,
  country,
  vatResponsible,
  vatStatus,
  taxId,
  taxExemptions,
  handleFormChange,
  setBillingDetailsForm,
  taxIdValidationResult,
  state,
}: TaxIdFieldProps) => {
  const {
    isCountryChile,
    isCountryPhilippines,
    isCountryThailand,
    isCountrySerbia,
    isCountryUkraine,
    isCountryUzbekistan,
    taxIdPlaceholder,
    VATRequiredMessageId,
    VATNotRequiredMessageId,
  } = getVATResponsibleCopies(country?.isoCountryCode);
  const shouldShowTaasError =
    taxId && (taxIdValidationResult?.failed || taxIdValidationResult?._result?.failureEntity);

  const isCountryEgypt = checkIsCountryEgypt(country?.isoCountryCode);
  const taxIdLabelUpdated = isCountryEgypt ? 'TRN' : taxIdLabel;
  const isCountryCanada = checkIsCountryCanada(country?.isoCountryCode);
  const taxIdLabelElement = (
    <>
      {taxIdLabelUpdated}
      {isCountryUkraine && <Help>{t('checkout.contact.tax-id-tooltip.ukraine') as string}</Help>}
    </>
  );

  let taxExemptionsLabel = '';

  if (isCountryEgypt) {
    taxExemptionsLabel = taxExemptionsLabels.Egypt;
  } else if (isCountryCanada) {
    taxExemptionsLabel = taxExemptionsLabels.Canada[state || ''];
  }
  return (
    <>
      <Field
        name="taxId"
        id="taxId"
        label={taxIdLabelElement}
        errorMessage={() =>
          t('checkout.contact.enter-valid-taxid', { taxIdLabel: taxIdLabelUpdated })
        }
      >
        {({ fieldProps }) => (
          <Textfield
            {...fieldProps}
            isDisabled={!showTaxId || !taxRate}
            maxLength={100}
            onChange={(e) => handleFormChange(e, 'taxId')}
            value={taxId}
            data-cy="mac-billing-details-tax-id-input"
            width={Dimensions.units(50)}
            placeholder={taxIdPlaceholder}
          />
        )}
      </Field>
      {shouldShowTaasError && (
        <ErrorMessage>
          {taxIdValidationResult._result?.failureEntity?.errorDetail || (
            <FormattedMessage
              defaultMessage="Invalid Tax ID"
              id="mac.payment-details.billing-form.invalid.tax.id"
            />
          )}
        </ErrorMessage>
      )}
      {taxRate && showTaxId && (
        <>
          <TaxFreeMessage taxRate={taxRate} country={country} />
          {(isCountryChile ||
            isCountryPhilippines ||
            isCountryThailand ||
            isCountrySerbia ||
            isCountryUkraine ||
            isCountryUzbekistan) && (
            <Field
              name={isCountryUkraine ? 'vatStatus' : 'vatResponsible'}
              isRequired
              aria-required
              errorMessage={() => t('checkout.contact.tax-id-vat-error-message')}
            >
              {({ fieldProps }) => (
                <BillingDetailsTaxIdRadio
                  {...fieldProps}
                  vatResponsible={vatResponsible}
                  vatStatus={vatStatus}
                  isTaxIdFieldEmpty={!taxId}
                  setBillingDetailsForm={setBillingDetailsForm}
                  VATRequiredMessageId={VATRequiredMessageId}
                  VATNotRequiredMessageId={VATNotRequiredMessageId}
                  isUkraine={isCountryUkraine}
                />
              )}
            </Field>
          )}
          {((isCountryEgypt && !!taxId) ||
            (isCountryCanada && ['MB', 'BC', 'QC', 'SK'].includes(state || ''))) && (
            <>
              <Field
                aria-required
                isRequired={isCountryEgypt}
                name="taxExemptions"
                id="taxExemptions"
                label={taxExemptionsLabel}
                errorMessage={() =>
                  t('checkout.contact.enter-valid-taxid', { taxIdLabel: taxExemptionsLabel })
                }
              >
                {({ fieldProps }) => (
                  <Textfield
                    {...fieldProps}
                    maxLength={100}
                    onChange={(e) => handleFormChange(e, 'taxExemptions')}
                    value={taxExemptions}
                    data-cy="mac-billing-details-tax-exemptions-input"
                    width={Dimensions.units(50)}
                  />
                )}
              </Field>
              {!taxExemptions && isCountryEgypt && (
                <ErrorMessage>
                  <FormattedMessage
                    defaultMessage="This field is required. It can not be empty"
                    id="mac.payment-details.billing-form.field-empty"
                  />
                </ErrorMessage>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
